/**
 * @file App.js
 * @brief Defines top-level authentication handling and route registration for the frontend application.
 */
import { useEffect, useRef, useState } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";

import Header        from "./components/Header";
import ListingList   from "./components/ListingList";
import ListingForm   from "./components/ListingForm";
import ListingDetail from "./components/ListingDetails";
import EditListing   from "./components/ListingEdit";
import UserProfile   from "./components/UserProfile";
import Account       from "./components/Account";
import AccountListings from "./components/AccountListings";
import Moderation    from "./components/Moderation";
import MessagesLayout from "./components/MessagesLayout";
import CatalogAdmin  from "./components/CatalogAdmin";
import { useSiteSettings } from "./context/SiteSettingsContext";
import { useUserProfile } from "./context/UserProfileContext";
import { useUnifiedAuth } from "./hooks/useUnifiedAuth";
import SiteSettings  from "./components/SiteSettings";

const API_BASE = process.env.REACT_APP_API_BASE_URL + "/api/v1";

/* ─── Main app ───────────────────────────────────────────────────────────── */

export default function App() {
    const auth = useUnifiedAuth();
    const { t } = useTranslation();
    const redirectingRef = useRef(false);

    /* ── Token lifecycle (SSO only) ── */
    useEffect(() => {
        if (auth.isLocalAuth) return;

        const onExpired = () => {
            if (redirectingRef.current) return;
            redirectingRef.current = true;
            auth.signinRedirect();
        };

        auth.events?.addAccessTokenExpired?.(onExpired);
        return () => auth.events?.removeAccessTokenExpired?.(onExpired);
    }, [auth]);

    useEffect(() => {
        if (auth.isLocalAuth) return;

        const msg = auth.error?.message ?? "";
        const isSessionError =
            msg.includes("Session not active") ||
            msg.includes("login_required") ||
            msg.includes("interaction_required") ||
            msg.includes("required client");

        if (!isSessionError) {
            redirectingRef.current = false;
            return;
        }

        if (!redirectingRef.current) {
            redirectingRef.current = true;
            auth.signinRedirect();
        }
    }, [auth]);

    /* ── Auth states ── */
    if (auth.isLoading) return <LoadingScreen />;

    if (!auth.isLocalAuth && auth.error) {
        const msg = auth.error.message ?? "";
        const isSessionError =
            msg.includes("Session not active")    ||
            msg.includes("login_required")        ||
            msg.includes("interaction_required")  ||
            msg.includes("required client");

        if (isSessionError) {
            return <LoadingScreen label={t("app.redirecting")} />;
        }

        // If SSO errored but local auth is available, fall through to login screen
        if (!auth.localEnabled) {
            return (
                <div className="auth-shell auth-error">
                    {t("app.authError")}: {auth.error.message}
                </div>
            );
        }
    }

    if (!auth.isAuthenticated) return <PublicOrLoginScreen />;

    /* ── Authenticated app ── */
    return <AuthenticatedApp />;
}

function AuthenticatedApp() {
    const { siteSettings } = useSiteSettings();
    const { profile } = useUserProfile();
    const { t } = useTranslation();
    const [motdDismissed, setMotdDismissed] = useState(false);

    const groups = profile?.groups || [];
    const isAdmin = groups.includes("admins");
    const maintenanceMode = siteSettings?.maintenance_mode === true;
    const motd = siteSettings?.motd || "";
    const siteName = siteSettings?.site_name || "LeBonTrain";

    if (maintenanceMode && !isAdmin) {
        return <MaintenanceScreen siteName={siteName} />;
    }

    return (
        <Router>
            <Header />
            {motd && !motdDismissed && (
                <div className="motd-banner">
                    <span className="motd-text">{motd}</span>
                    <button
                        className="motd-dismiss"
                        onClick={() => setMotdDismissed(true)}
                        aria-label={t("common.clear")}
                    >
                        &times;
                    </button>
                </div>
            )}
            {maintenanceMode && isAdmin && (
                <div className="motd-banner motd-banner--warning">
                    <span className="motd-text">{t("app.maintenanceTitle")} — {t("app.maintenanceMessage")}</span>
                </div>
            )}
            <Routes>
                <Route path="/"                   element={<ListingList />} />
                <Route path="/listings/:id"        element={<ListingDetail />} />
                <Route path="/profile/:userId"     element={<UserProfile />} />

                <Route path="/listings/new"        element={<ListingForm />} />
                <Route path="/listings/:id/edit"   element={<EditListing />} />
                <Route path="/account/home"        element={<Account />} />
                <Route path="/account/listings"    element={<AccountListings />} />
                <Route path="/favorites"           element={<ListingList key="favorites" showFavorites />} />
                <Route path="/moderation"          element={<Moderation />} />
                <Route path="/admin/catalog"        element={<CatalogAdmin />} />
                <Route path="/admin/settings"      element={<SiteSettings />} />
                <Route path="/messages"            element={<MessagesLayout />} />
                <Route path="/messages/:listingId/:otherUserId" element={<MessagesLayout />} />

                {/* Fallback */}
                <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
        </Router>
    );
}

/* ─── Screens ────────────────────────────────────────────────────────────── */

function LoadingScreen({ label }) {
    const { t } = useTranslation();
    return (
        <div className="auth-shell">
            <div className="auth-spinner" />
            <p className="auth-subtitle">{label || t("common.loading")}</p>
        </div>
    );
}

function MaintenanceScreen({ siteName }) {
    const { t } = useTranslation();
    return (
        <div className="auth-shell">
            <h1 className="auth-title">
                <img
                    src={`${process.env.PUBLIC_URL}/favicon.svg`}
                    alt={`${siteName} icon`}
                    className="auth-title-icon"
                />
                <span>{siteName}</span>
            </h1>
            <p className="auth-subtitle" style={{ fontSize: "1.1rem", marginTop: 12 }}>
                {t("app.maintenanceTitle")}
            </p>
            <p className="auth-subtitle">
                {t("app.maintenanceMessage")}
            </p>
        </div>
    );
}

function PublicOrLoginScreen() {
    const { siteSettings, loading } = useSiteSettings();
    if (loading) return <LoadingScreen />;
    if (siteSettings?.listings_public) return <PublicApp />;
    return <LoginScreen />;
}

function PublicApp() {
    const auth = useUnifiedAuth();
    const { t } = useTranslation();

    return (
        <Router>
            <Header />
            <div className="motd-banner motd-banner--public-cta">
                <span className="motd-text">
                    {t("app.signInMessage")}
                </span>
                {auth.ssoEnabled && (
                    <button
                        className="auth-cta-inline"
                        onClick={() => auth.signinRedirect()}
                    >
                        {t("app.signIn")}
                    </button>
                )}
            </div>
            <Routes>
                <Route path="/" element={<ListingList />} />
                <Route path="/listings/:id" element={<ListingDetail />} />
                <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
        </Router>
    );
}

function LoginScreen() {
    const auth = useUnifiedAuth();
    const { t } = useTranslation();
    const { siteSettings } = useSiteSettings();
    const siteName = siteSettings?.site_name || "LeBonTrain";
    const ssoEnabled = auth.ssoEnabled;
    const localEnabled = auth.localEnabled;

    const [mode, setMode] = useState("login"); // "login" | "register"
    const [username, setUsername] = useState("");
    const [password, setPassword] = useState("");
    const [email, setEmail] = useState("");
    const [error, setError] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    const handleLocalSubmit = async (e) => {
        e.preventDefault();
        setError(null);
        setSubmitting(true);

        try {
            const endpoint = mode === "register"
                ? `${API_BASE}/auth/register/`
                : `${API_BASE}/auth/login/`;
            const payload = mode === "register"
                ? { username, password, email }
                : { username, password };

            const { data } = await axios.post(endpoint, payload);
            auth.local.login(data.user, data.token);
        } catch (err) {
            const detail = err.response?.data?.detail || err.response?.data?.error;
            if (detail) {
                setError(detail);
            } else {
                setError(mode === "register" ? t("localAuth.registerFailed") : t("localAuth.loginFailed"));
            }
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="auth-shell">
            <h1 className="auth-title">
                <img
                    src={`${process.env.PUBLIC_URL}/favicon.svg`}
                    alt={`${siteName} icon`}
                    className="auth-title-icon"
                />
                <span>{siteName}</span>
            </h1>
            <p className="auth-subtitle auth-subtitle-login">
                {t("app.signInMessage")}
            </p>

            {/* SSO button */}
            {ssoEnabled && (
                <button
                    onClick={() => auth.signinRedirect()}
                    className="auth-cta"
                >
                    {t("app.signIn")}
                </button>
            )}

            {/* Divider when both enabled */}
            {ssoEnabled && localEnabled && (
                <div className="auth-divider">
                    <span>{t("localAuth.or")}</span>
                </div>
            )}

            {/* Local login/register form */}
            {localEnabled && (
                <form className="auth-local-form" onSubmit={handleLocalSubmit}>
                    <div className="auth-tabs">
                        <button
                            type="button"
                            className={`auth-tab ${mode === "login" ? "auth-tab--active" : ""}`}
                            onClick={() => { setMode("login"); setError(null); }}
                        >
                            {t("localAuth.login")}
                        </button>
                        <button
                            type="button"
                            className={`auth-tab ${mode === "register" ? "auth-tab--active" : ""}`}
                            onClick={() => { setMode("register"); setError(null); }}
                        >
                            {t("localAuth.register")}
                        </button>
                    </div>

                    <input
                        className="auth-input"
                        type="text"
                        placeholder={t("localAuth.username")}
                        value={username}
                        onChange={(e) => setUsername(e.target.value)}
                        required
                        autoComplete="username"
                    />
                    {mode === "register" && (
                        <input
                            className="auth-input"
                            type="email"
                            placeholder={t("localAuth.email")}
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            autoComplete="email"
                        />
                    )}
                    <input
                        className="auth-input"
                        type="password"
                        placeholder={t("localAuth.password")}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        autoComplete={mode === "register" ? "new-password" : "current-password"}
                    />

                    {error && <p className="auth-form-error">{error}</p>}

                    <button
                        type="submit"
                        className="auth-cta"
                        disabled={submitting}
                    >
                        {submitting
                            ? t("common.loading")
                            : mode === "register"
                                ? t("localAuth.register")
                                : t("localAuth.login")
                        }
                    </button>
                </form>
            )}
        </div>
    );
}
